import { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Voucher } from "@/lib/accounting/types";
import {
  CreditCard, Link2, Copy, CheckCircle2, Clock, XCircle, ExternalLink,
  IndianRupee, Globe, Shield, Zap, QrCode, Send, ArrowUpRight,
  ArrowDownLeft, RefreshCw, Eye, EyeOff, Settings, Smartphone
} from "lucide-react";

// ── Types ────────────────────────────────────────────────────────

export type PaymentGatewayId = "razorpay" | "stripe" | "payu";
export type PaymentStatus = "pending" | "success" | "failed" | "refunded" | "expired";

export interface GatewayConfig {
  id: PaymentGatewayId;
  name: string;
  logo: string;
  description: string;
  isConnected: boolean;
  environment: "test" | "live";
  keyId?: string;
  keySecret?: string;
  webhookUrl?: string;
  supportedMethods: string[];
  fees: string;
  settlementDays: string;
}

export interface PaymentLink {
  id: string;
  gateway: PaymentGatewayId;
  voucherId: string;
  voucherNumber: string;
  partyName: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  url: string;
  shortUrl: string;
  createdAt: string;
  expiresAt: string;
  paidAt?: string;
  paymentId?: string;
  method?: string;
}

export interface PaymentRecord {
  id: string;
  linkId: string;
  gateway: PaymentGatewayId;
  voucherId: string;
  voucherNumber: string;
  partyName: string;
  amount: number;
  fees: number;
  netAmount: number;
  status: PaymentStatus;
  method: string;
  transactionId: string;
  paidAt: string;
  settledAt?: string;
  isRecorded: boolean;
}

// ── Seed Data ────────────────────────────────────────────────────

const GATEWAY_CONFIGS: GatewayConfig[] = [
  {
    id: "razorpay", name: "Razorpay", logo: "₹",
    description: "India's leading payment gateway — UPI, cards, netbanking, wallets",
    isConnected: true, environment: "live",
    keyId: "rzp_live_****x9Kf",
    webhookUrl: "https://api.accoupro.in/webhooks/razorpay",
    supportedMethods: ["UPI", "Credit Card", "Debit Card", "Net Banking", "Wallets", "EMI", "Pay Later"],
    fees: "2% + ₹3 per txn", settlementDays: "T+2",
  },
  {
    id: "stripe", name: "Stripe", logo: "S",
    description: "Global payment processing for international clients & subscriptions",
    isConnected: false, environment: "test",
    supportedMethods: ["Credit Card", "Debit Card", "Apple Pay", "Google Pay", "SEPA", "Wire Transfer"],
    fees: "2.9% + $0.30 per txn", settlementDays: "T+2",
  },
  {
    id: "payu", name: "PayU", logo: "P",
    description: "Full-stack payments with EMI, UPI, cards & net banking for India",
    isConnected: false, environment: "test",
    supportedMethods: ["UPI", "Credit Card", "Debit Card", "Net Banking", "Wallets", "EMI", "BNPL"],
    fees: "1.99% + ₹3 per txn", settlementDays: "T+3",
  },
];

const SEED_PAYMENT_LINKS: PaymentLink[] = [];
const SEED_PAYMENTS: PaymentRecord[] = [];

// ── Status Helpers ───────────────────────────────────────────────

const STATUS_CONFIG: Record<PaymentStatus, { label: string; className: string; icon: typeof CheckCircle2 }> = {
  pending: { label: "Pending", className: "text-warning bg-warning/10", icon: Clock },
  success: { label: "Paid", className: "text-success bg-success/10", icon: CheckCircle2 },
  failed: { label: "Failed", className: "text-destructive bg-destructive/10", icon: XCircle },
  refunded: { label: "Refunded", className: "text-muted-foreground bg-muted", icon: RefreshCw },
  expired: { label: "Expired", className: "text-muted-foreground bg-muted/50", icon: Clock },
};

function PaymentStatusBadge({ status }: { status: PaymentStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.className}`}>
      <cfg.icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
}

// ── Gateway Setup Dialog ─────────────────────────────────────────

function GatewaySetupDialog({
  gateway,
  open,
  onOpenChange,
  onConnect,
}: {
  gateway: GatewayConfig;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onConnect: (id: PaymentGatewayId, keyId: string, keySecret: string, env: "test" | "live") => void;
}) {
  const [keyId, setKeyId] = useState("");
  const [keySecret, setKeySecret] = useState("");
  const [env, setEnv] = useState<"test" | "live">("test");
  const [showSecret, setShowSecret] = useState(false);

  const keyLabels: Record<PaymentGatewayId, { id: string; secret: string }> = {
    razorpay: { id: "Key ID", secret: "Key Secret" },
    stripe: { id: "Publishable Key", secret: "Secret Key" },
    payu: { id: "Merchant Key", secret: "Merchant Salt" },
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
              {gateway.logo}
            </div>
            Connect {gateway.name}
          </DialogTitle>
          <DialogDescription>{gateway.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Environment</Label>
            <Select value={env} onValueChange={(v) => setEnv(v as "test" | "live")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="test">🧪 Test / Sandbox</SelectItem>
                <SelectItem value="live">🟢 Live / Production</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{keyLabels[gateway.id].id}</Label>
            <Input placeholder={`Enter ${keyLabels[gateway.id].id.toLowerCase()}`} value={keyId} onChange={(e) => setKeyId(e.target.value)} className="font-mono text-sm" />
          </div>
          <div className="space-y-2">
            <Label>{keyLabels[gateway.id].secret}</Label>
            <div className="relative">
              <Input
                type={showSecret ? "text" : "password"}
                placeholder={`Enter ${keyLabels[gateway.id].secret.toLowerCase()}`}
                value={keySecret}
                onChange={(e) => setKeySecret(e.target.value)}
                className="font-mono text-sm pr-10"
              />
              <Button size="sm" variant="ghost" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0" onClick={() => setShowSecret(!showSecret)}>
                {showSecret ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
          <div className="p-3 rounded-lg bg-muted text-xs text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">Supported Methods:</p>
            <div className="flex flex-wrap gap-1">
              {gateway.supportedMethods.map((m) => (
                <Badge key={m} variant="outline" className="text-[10px]">{m}</Badge>
              ))}
            </div>
            <p className="mt-2">Fees: {gateway.fees} • Settlement: {gateway.settlementDays}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!keyId || !keySecret} onClick={() => { onConnect(gateway.id, keyId, keySecret, env); onOpenChange(false); }}>
            <Shield className="h-4 w-4 mr-1" /> Connect Securely
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Create Payment Link Dialog ───────────────────────────────────

export function CreatePaymentLinkDialog({
  voucher,
  open,
  onOpenChange,
  onCreated,
}: {
  voucher: Voucher;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated?: (link: PaymentLink) => void;
}) {
  const [gateway, setGateway] = useState<PaymentGatewayId>("razorpay");
  const [expDays, setExpDays] = useState("7");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);

  const handleCreate = () => {
    setCreating(true);
    setTimeout(() => {
      const link: PaymentLink = {
        id: `pl-${crypto.randomUUID().slice(0, 8)}`,
        gateway,
        voucherId: voucher.id,
        voucherNumber: voucher.invoiceNumber || voucher.voucherNumber,
        partyName: voucher.partyName || "Customer",
        amount: voucher.totalDebit,
        currency: "INR",
        status: "pending",
        url: `https://${gateway === "razorpay" ? "rzp.io" : gateway === "stripe" ? "checkout.stripe.com" : "payu.in"}/l/${crypto.randomUUID().slice(0, 6)}`,
        shortUrl: `https://${gateway === "razorpay" ? "rzp.io" : gateway === "stripe" ? "stripe.com" : "payu.in"}/l/${crypto.randomUUID().slice(0, 6)}`,
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + parseInt(expDays) * 86400000).toISOString(),
      };
      onCreated?.(link);
      setCreating(false);
      onOpenChange(false);
      toast.success(`Payment link created via ${gateway === "razorpay" ? "Razorpay" : gateway === "stripe" ? "Stripe" : "PayU"}`);
    }, 1200);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" /> Create Payment Link
          </DialogTitle>
          <DialogDescription>
            Generate a secure payment link for {voucher.invoiceNumber || voucher.voucherNumber}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 rounded-lg bg-muted/50 border border-border">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-foreground">{voucher.invoiceNumber || voucher.voucherNumber}</p>
                <p className="text-xs text-muted-foreground">{voucher.partyName || "Customer"}</p>
              </div>
              <p className="text-lg font-bold text-foreground">₹{voucher.totalDebit.toLocaleString("en-IN")}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Payment Gateway</Label>
            <Select value={gateway} onValueChange={(v) => setGateway(v as PaymentGatewayId)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="razorpay">
                  <span className="flex items-center gap-2">💳 Razorpay — UPI, Cards, Net Banking</span>
                </SelectItem>
                <SelectItem value="stripe">
                  <span className="flex items-center gap-2">🌐 Stripe — International Cards, Apple/Google Pay</span>
                </SelectItem>
                <SelectItem value="payu">
                  <span className="flex items-center gap-2">🏦 PayU — UPI, Cards, EMI, BNPL</span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Link Expires In</Label>
            <Select value={expDays} onValueChange={setExpDays}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 Days</SelectItem>
                <SelectItem value="7">7 Days</SelectItem>
                <SelectItem value="15">15 Days</SelectItem>
                <SelectItem value="30">30 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Input placeholder="Payment for March invoice" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating}>
            {creating ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Link2 className="h-4 w-4 mr-1" />}
            {creating ? "Creating..." : "Create Payment Link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Payment Link Quick Button (for invoice rows) ─────────────────

export function PaymentLinkButton({
  voucher,
  onLinkCreated,
}: {
  voucher: Voucher;
  onLinkCreated?: (link: PaymentLink) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Create Payment Link" onClick={() => setOpen(true)}>
        <Link2 className="h-3.5 w-3.5 text-primary" />
      </Button>
      <CreatePaymentLinkDialog voucher={voucher} open={open} onOpenChange={setOpen} onCreated={onLinkCreated} />
    </>
  );
}

// ── Payment Dashboard Component ──────────────────────────────────

export function PaymentGatewayDashboard() {
  const [gateways, setGateways] = useState<GatewayConfig[]>(GATEWAY_CONFIGS);
  const [paymentLinks, setPaymentLinks] = useState<PaymentLink[]>(SEED_PAYMENT_LINKS);
  const [payments, setPayments] = useState<PaymentRecord[]>(SEED_PAYMENTS);
  const [setupGateway, setSetupGateway] = useState<GatewayConfig | null>(null);
  const [activeTab, setActiveTab] = useState("overview");

  const handleConnect = useCallback((id: PaymentGatewayId, keyId: string, _keySecret: string, env: "test" | "live") => {
    setGateways((prev) => prev.map((g) =>
      g.id === id ? { ...g, isConnected: true, environment: env, keyId: `${id.slice(0, 3)}_****${keyId.slice(-4)}` } : g
    ));
    toast.success(`${id.charAt(0).toUpperCase() + id.slice(1)} connected in ${env} mode`);
  }, []);

  const handleSimulatePayment = useCallback((linkId: string) => {
    const link = paymentLinks.find((l) => l.id === linkId);
    if (!link || link.status !== "pending") return;

    const methods = ["UPI (Google Pay)", "UPI (PhonePe)", "Credit Card (Visa)", "Debit Card (RuPay)", "Net Banking (HDFC)"];
    const method = methods[Math.floor(Math.random() * methods.length)];
    const fees = Math.round(link.amount * 0.02);

    setPaymentLinks((prev) => prev.map((l) =>
      l.id === linkId ? { ...l, status: "success" as PaymentStatus, paidAt: new Date().toISOString(), paymentId: `pay_${crypto.randomUUID().slice(0, 8)}`, method } : l
    ));

    const payment: PaymentRecord = {
      id: `pr-${crypto.randomUUID().slice(0, 8)}`,
      linkId,
      gateway: link.gateway,
      voucherId: link.voucherId,
      voucherNumber: link.voucherNumber,
      partyName: link.partyName,
      amount: link.amount,
      fees,
      netAmount: link.amount - fees,
      status: "success",
      method,
      transactionId: `pay_${crypto.randomUUID().slice(0, 8)}`,
      paidAt: new Date().toISOString(),
      isRecorded: false,
    };
    setPayments((prev) => [payment, ...prev]);
    toast.success(`Payment of ₹${link.amount.toLocaleString("en-IN")} received from ${link.partyName}`);
  }, [paymentLinks]);

  const handleRecordPayment = useCallback((paymentId: string) => {
    setPayments((prev) => prev.map((p) =>
      p.id === paymentId ? { ...p, isRecorded: true, settledAt: new Date().toISOString() } : p
    ));
    toast.success("Payment recorded in accounting system — Receipt voucher created");
  }, []);

  const stats = {
    totalCollected: payments.filter((p) => p.status === "success").reduce((s, p) => s + p.amount, 0),
    totalFees: payments.filter((p) => p.status === "success").reduce((s, p) => s + p.fees, 0),
    pendingLinks: paymentLinks.filter((l) => l.status === "pending").length,
    successRate: paymentLinks.length > 0
      ? Math.round((paymentLinks.filter((l) => l.status === "success").length / paymentLinks.length) * 100)
      : 0,
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total Collected", value: `₹${stats.totalCollected.toLocaleString("en-IN")}`, icon: ArrowDownLeft, color: "text-success" },
          { label: "Gateway Fees", value: `₹${stats.totalFees.toLocaleString("en-IN")}`, icon: CreditCard, color: "text-warning" },
          { label: "Pending Links", value: stats.pendingLinks, icon: Clock, color: "text-primary" },
          { label: "Success Rate", value: `${stats.successRate}%`, icon: Zap, color: "text-accent" },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-3 sm:p-4 flex items-center gap-3">
              <s.icon className={`h-5 w-5 ${s.color}`} />
              <div>
                <p className="text-lg font-bold text-foreground">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Gateways</TabsTrigger>
          <TabsTrigger value="links">Payment Links ({paymentLinks.length})</TabsTrigger>
          <TabsTrigger value="payments">Payments ({payments.length})</TabsTrigger>
        </TabsList>

        {/* ── Gateways Tab ── */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid gap-4 md:grid-cols-3">
            {gateways.map((gw) => (
              <Card key={gw.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold">
                        {gw.logo}
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{gw.name}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{gw.description}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1">
                    {gw.supportedMethods.slice(0, 4).map((m) => (
                      <Badge key={m} variant="outline" className="text-[10px]">{m}</Badge>
                    ))}
                    {gw.supportedMethods.length > 4 && (
                      <Badge variant="outline" className="text-[10px]">+{gw.supportedMethods.length - 4}</Badge>
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Fees: {gw.fees}</p>
                    <p>Settlement: {gw.settlementDays}</p>
                  </div>

                  {gw.isConnected ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge className="gap-1 bg-success/10 text-success border-success/20">
                          <CheckCircle2 className="h-3 w-3" /> Connected
                        </Badge>
                        <Badge variant="outline" className="text-[10px]">{gw.environment}</Badge>
                      </div>
                      {gw.keyId && <p className="text-xs font-mono text-muted-foreground">Key: {gw.keyId}</p>}
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => setSetupGateway(gw)}>
                          <Settings className="h-3 w-3 mr-1" /> Settings
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => {
                          setGateways((prev) => prev.map((g) => g.id === gw.id ? { ...g, isConnected: false, keyId: undefined } : g));
                          toast.success(`${gw.name} disconnected`);
                        }}>
                          Disconnect
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button className="w-full" onClick={() => setSetupGateway(gw)}>
                      <Shield className="h-4 w-4 mr-1" /> Connect {gw.name}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ── Payment Links Tab ── */}
        <TabsContent value="links" className="space-y-4 mt-4">
          <div className="rounded-lg bg-card border border-border shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Invoice</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Party</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Gateway</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Amount</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Created</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentLinks.map((link) => (
                    <tr key={link.id} className="border-t border-border/50 hover:bg-muted/30">
                      <td className="py-3 px-4 font-medium text-[#2563EB] dark:text-blue-400 hover:text-blue-700 transition-colors">{link.voucherNumber}</td>
                      <td className="py-3 px-4 text-foreground">{link.partyName}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="text-[10px] capitalize">{link.gateway}</Badge>
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-foreground">₹{link.amount.toLocaleString("en-IN")}</td>
                      <td className="py-3 px-4"><PaymentStatusBadge status={link.status} /></td>
                      <td className="py-3 px-4 text-xs text-muted-foreground">{new Date(link.createdAt).toLocaleDateString("en-IN")}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Copy Link"
                            onClick={() => { navigator.clipboard.writeText(link.shortUrl); toast.success("Payment link copied"); }}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Share via WhatsApp"
                            onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Pay ₹${link.amount.toLocaleString("en-IN")} for invoice ${link.voucherNumber}: ${link.shortUrl}`)}`, "_blank")}>
                            <Send className="h-3.5 w-3.5 text-success" />
                          </Button>
                          {link.status === "pending" && (
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => handleSimulatePayment(link.id)}>
                              <Zap className="h-3 w-3 mr-1" /> Simulate Pay
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {paymentLinks.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-12 text-center">
                        <Link2 className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                        <p className="text-muted-foreground text-sm">No payment links created yet</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* ── Payments Tab ── */}
        <TabsContent value="payments" className="space-y-4 mt-4">
          <div className="rounded-lg bg-card border border-border shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Transaction</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Invoice</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Party</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Method</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Amount</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Fees</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Net</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Recorded</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-t border-border/50 hover:bg-muted/30">
                      <td className="py-3 px-4">
                        <p className="font-mono text-xs text-accent">{p.transactionId}</p>
                        <p className="text-[10px] text-muted-foreground">{new Date(p.paidAt).toLocaleString("en-IN")}</p>
                      </td>
                      <td className="py-3 px-4 font-medium text-foreground">{p.voucherNumber}</td>
                      <td className="py-3 px-4 text-foreground">{p.partyName}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="text-[10px]">{p.method}</Badge>
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-foreground">₹{p.amount.toLocaleString("en-IN")}</td>
                      <td className="py-3 px-4 text-right text-warning text-xs">-₹{p.fees.toLocaleString("en-IN")}</td>
                      <td className="py-3 px-4 text-right font-medium text-success">₹{p.netAmount.toLocaleString("en-IN")}</td>
                      <td className="py-3 px-4">
                        {p.isRecorded ? (
                          <Badge className="gap-1 bg-success/10 text-success border-success/20 text-[10px]">
                            <CheckCircle2 className="h-2.5 w-2.5" /> Recorded
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="gap-1 text-[10px] text-warning">
                            <Clock className="h-2.5 w-2.5" /> Pending
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {!p.isRecorded && (
                          <Button size="sm" className="h-7 text-xs gap-1" onClick={() => handleRecordPayment(p.id)}>
                            <CheckCircle2 className="h-3 w-3" /> Record in Books
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {payments.length === 0 && (
                    <tr>
                      <td colSpan={9} className="py-12 text-center">
                        <IndianRupee className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                        <p className="text-muted-foreground text-sm">No payments received yet</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Setup Dialog */}
      {setupGateway && (
        <GatewaySetupDialog
          gateway={setupGateway}
          open={!!setupGateway}
          onOpenChange={(o) => !o && setSetupGateway(null)}
          onConnect={handleConnect}
        />
      )}
    </div>
  );
}
